import React from 'react';
import PropTypes from 'prop-types';
import {
  Elements,
  CardElement,
  injectStripe,
  PaymentRequestButtonElement,
} from 'react-stripe-elements';
import get from 'lodash/get';
import { EMPTY_OBJECT } from '../../constants';
import Button from '../Button';
import ElementTransition from '../ElementTransition';
import Link from '../Link';
import { Form, Input } from '../Form';
import Style from '../Style';
import { H3, H5, H6, L3, P1, P2 } from '../Typography';

const MAX_STRIPE_HEIGHT = '3rem';

class Payment extends React.PureComponent {
  static propTypes = {
    style: PropTypes.object,
    allowCoupon: PropTypes.bool,
    paymentOnFile: PropTypes.bool,
    paymentInfoHeadline: PropTypes.element,
    chosenPlan: PropTypes.object,
    couponCode: PropTypes.string,
    couponError: PropTypes.string,
    paymentError: PropTypes.string,
    planSummary: PropTypes.object,
    onGoToPlan: PropTypes.func,
    onChangeCoupon: PropTypes.func,
    onCreateSubscription: PropTypes.func,
    onCardFocus: PropTypes.func,
    showCreateButton: PropTypes.bool,
    createSubscriptionRef: PropTypes.func,
    fetchingUpdated: PropTypes.func,
  };
  static defaultProps = {
    style: EMPTY_OBJECT,
    allowCoupon: true,
    paymentOnFile: false,
    onCardFocus: () => {},
    showCreateButton: true,
    createSubscriptionRef: subFunc => {},
    fetchingUpdated: fetching => {},
  };

  constructor(props) {
    super(props);

    this.state = {
      showCC: !props.paymentOnFile,
      formEnabled: true,
      fetching: false,
    };
  }

  componentDidMount() {
    this.props.createSubscriptionRef(this.doSubmit);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.fetching !== this.state.fetching) {
      this.props.fetchingUpdated(this.state.fetching);
    }
  }

  completeSubscription = token => {
    const tokenId = get(token, 'id');
    if (tokenId === undefined && !this.props.paymentOnFile) {
      return;
    }

    let couponCode;
    if (this.props.allowCoupon) {
      couponCode = this.props.couponCode;
    }

    if (!this.state.formEnabled) {
      return;
    }
    this.setState({ formEnabled: false, fetching: true });
    this.props
      .onCreateSubscription(tokenId, this.props.chosenPlan, couponCode)
      .then(() => this.setState({ formEnabled: true, fetching: false }))
      .catch(() => this.setState({ formEnabled: true, fetching: false }));
  };

  doSubmit = e => {
    if (this.state.showCC) {
      const fakeEvent = { preventDefault: () => {} };
      // HACK: To make sure that any error from stripe propogates to state. Users wont notice.
      setTimeout(() => this.cardSubmit(fakeEvent), 100);
    } else {
      this.completeSubscription();
    }
  };

  headline = () => {
    if (this.props.paymentOnFile) {
      return (
        <div>
          <H3>Confirm your subscription change</H3>
          {this.state.showCC && (
            <P1 style={{ color: Style.Colors.shadow4 }}>
              Enter your new card details to change your payment method.{' '}
              <Link
                underline={true}
                onClick={e => this.setState({ showCC: false })}
              >
                Cancel.
              </Link>
            </P1>
          )}
          {!this.state.showCC && (
            <P1 style={{ color: Style.Colors.shadow4 }}>
              We'll use the card you have on file unless you{' '}
              <Link
                underline={true}
                onClick={e => this.setState({ showCC: true })}
              >
                change it here.
              </Link>
            </P1>
          )}
        </div>
      );
    } else {
      return (
        <div>
          <H3>
            {this.props.paymentInfoHeadline || (
              <span>Enter your payment information</span>
            )}
          </H3>
          <P1 style={{ color: Style.Colors.shadow4 }}>
            Everything is encrypted and no billing information is ever stored on
            our&nbsp;servers.
          </P1>
        </div>
      );
    }
  };

  render() {
    const {
      style,
      allowCoupon,
      paymentOnFile,
      paymentInfoHeadline,
      chosenPlan,
      couponCode,
      couponError,
      paymentError,
      planSummary,
      onGoToPlan,
      onChangeCoupon,
      onCreateSubscription,
      onCardFocus,
      showCreateButton,
      ...otherProps
    } = this.props;

    let paymentMethod = {
      id: 'editPayment',
      element: <div style={{ height: MAX_STRIPE_HEIGHT }} />,
    };
    if (this.state.showCC) {
      paymentMethod = {
        id: 'cc',
        element: (
          <div style={{ maxHeight: MAX_STRIPE_HEIGHT }}>
            <Elements>
              <CardForm
                allowEmpty={paymentOnFile}
                onFocus={onCardFocus}
                onSubmit={this.completeSubscription}
                submitRef={func => (this.cardSubmit = func)}
              />
            </Elements>
          </div>
        ),
      };
    }

    return (
      <div style={{ padding: '7rem 5rem 5rem', ...style }}>
        <div style={{ maxWidth: '55rem' }}>
          {this.headline()}
          <div style={{ maxHeight: MAX_STRIPE_HEIGHT, margin: '3rem 0 -2rem' }}>
            <ElementTransition {...paymentMethod} />
            {paymentError && <P1>{paymentError}</P1>}
          </div>
          <div>
            <H5 style={{ color: Style.Colors.shadow2, margin: '9rem 0 0' }}>
              Summary
            </H5>
            <P1 style={{ color: Style.Colors.shadow4 }}>
              Here’s what you’re subscribing to, and paying today
            </P1>
            <L3
              style={{
                color: Style.Colors.shadow3,
                display: 'block',
                margin: '2rem 0 0.5rem',
              }}
            >
              YOUR{paymentOnFile ? ' ' : ' NEW '}PLAN
            </L3>
            <div style={{ color: Style.Colors.shadow2 }}>
              {planSummary === undefined && <P1>Fetching pricing...</P1>}
              {planSummary !== undefined && (
                <div>
                  <div>
                    <SummaryText>
                      {planSummary.name} - ${simpleRound(planSummary.monthly, 2)}/mo,
                      {chosenPlan.annual && ' paying annually'}
                    </SummaryText>
                    {onGoToPlan && (
                      <Action onClick={onGoToPlan}>Change this plan</Action>
                    )}
                  </div>
                  <div style={{ margin: '0 0 0.75rem' }}>
                    <SummaryText>
                      Amount due today = ${simpleRound(planSummary.total, 2)}
                    </SummaryText>
                  </div>
                  {allowCoupon && (
                    <div style={{ minHeight: '5.7rem' }}>
                      <CouponCode
                        summary={planSummary}
                        code={couponCode}
                        error={couponError}
                        onSubmit={onChangeCoupon}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {showCreateButton && (
          <div style={{ margin: '1rem 0 0', textAlign: 'center' }}>
            <Button
              size="large"
              onClick={this.doSubmit}
              working={this.state.fetching}
            >
              {paymentOnFile ? 'Update' : 'Create'}&nbsp;subscription
            </Button>
          </div>
        )}
      </div>
    );
  }
}

export default Payment;

class CardBase extends React.PureComponent {
  static propTypes = {
    allowEmpty: PropTypes.bool,
    onFocus: PropTypes.func,
    onSubmit: PropTypes.func,
    submitRef: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      error: undefined,
      empty: true,
      complete: false,
    };
  }

  componentDidMount() {
    this.props.submitRef(this.handleSubmit);
  }

  handleSubmit = e => {
    // We don't want to let default form submission happen here, which would refresh the page.
    e.preventDefault();

    // Errors always block submit.
    if (this.state.error !== undefined) {
      console.error('Stripe submit error', this.state.error);
      return;
    }

    if (this.state.empty) {
      if (this.props.allowEmpty) {
        // If empty is allowed and the form is empty we can submit without any token creation.
        this.props.onSubmit();
      } else {
        // If payment info is required and the form is empty, tell them.
        this.setState({ error: 'A valid credit card is required' });
      }

      // Empty forms cannot generate tokens.
      return;
    } else {
      if (!this.state.complete) {
        this.setState({
          error: 'You have not entered all the information yet',
        });
        return;
      }
    }

    // Within the context of `Elements`, this call to createToken knows which Element to
    // tokenize, since there's only one in this group.
    this.props.stripe
      .createToken()
      .then(result => {
        if (result.token) {
          this.props.onSubmit(result.token);
          return;
        }

        if (result.error) {
          this.setState({ error: result.error.message || result.error });
          return;
        }

        this.setState({
          error:
            'Something went wrong billing your card. You have not been charged.',
        });
      })
      .catch(err => {
        this.setState({
          error:
            'Something went wrong billing your card. You have not been charged.',
        });
      });
  };

  handleChange = e => {
    let message;
    if (e.error) {
      message = e.error.message;
    }

    const stateUpdate = { error: undefined };

    if (this.state.error !== message) {
      stateUpdate['error'] = message;
    }
    if (this.state.empty !== e.empty) {
      stateUpdate['empty'] = e.empty;
      stateUpdate['error'] = undefined;
    }
    if (this.state.complete !== e.complete) {
      stateUpdate['complete'] = e.complete;
    }

    this.setState(stateUpdate);
  };

  render() {
    const {
      allowEmpty,
      onFocus,
      onSubmit,
      submitRef,
      ...otherProps
    } = this.props;

    return (
      <div style={{ maxHeight: MAX_STRIPE_HEIGHT }}>
        <form
          style={{ maxHeight: MAX_STRIPE_HEIGHT }}
          onSubmit={this.handleSubmit}
        >
          <CardElement
            onFocus={onFocus}
            onChange={this.handleChange}
            hidePostalCode={true}
            style={{
              base: {
                fontSize: '22px',
                color: Style.Colors.shadow3,
                letterSpacing: '0.025em',
                fontFamily: 'Calibre, Source Code Pro, monospace',
                '::placeholder': {
                  color: Style.Colors.shadow4,
                },
              },
              invalid: {
                color: Style.Colors.error,
              },
            }}
          />
        </form>
        <div>
          <span style={{ color: Style.Colors.error }}>{this.state.error}</span>
        </div>
      </div>
    );
  }
}

export const CardForm = injectStripe(CardBase);

class CouponCode extends React.PureComponent {
  static propTypes = {
    summary: PropTypes.object,
    code: PropTypes.string,
    error: PropTypes.string,
    onSubmit: PropTypes.func,
  };
  static defaultProps = {};

  constructor(props) {
    super(props);

    this.state = {
      formEnabled: true,
      fetching: false,
      code: props.code,
    };
  }

  addCode = e => {
    this.setState({ code: '' });
  };

  removeCode = e => {
    this.setState({ code: undefined });
    this.props.onSubmit(undefined);
  };

  applyCode = () => {
    if (!this.state.formEnabled) {
      return;
    }

    if (this.state.code === '') {
      this.setState({ code: undefined });
      this.props.onSubmit(undefined);
    } else {
      this.setState({ formEnabled: false, fetching: true });
      this.props
        .onSubmit(this.state.code)
        .then(() => this.setState({ formEnabled: true, fetching: false }))
        .catch(() => this.setState({ formEnabled: true, fetching: false }));
    }
  };

  updateCode = value => {
    this.setState({ code: value });
  };

  render() {
    const { summary, code, error, onSubmit, ...otherProps } = this.props;

    if (this.state.code === undefined) {
      return (
        <div>
          <SummaryText>Have a coupon code?</SummaryText>
          <Action onClick={this.addCode}>Add it</Action>
        </div>
      );
    } else if (this.state.code === code && !this.state.fetching) {
      const amtPrefix = summary.amount_off ? `\$${summary.amount_off}` : '';
      const pctPrefix = summary.percent_off ? `${summary.percent_off}%` : '';
      const regularPostfix = summary.regularMonthly
        ? `(\$${simpleRound(summary.regularMonthly)}/mo after that)`
        : '';
      return (
        <div>
          <SummaryText>
            {amtPrefix}
            {pctPrefix} off {summary.discountPostfix} {regularPostfix}
          </SummaryText>
          <Action onClick={this.removeCode}>Remove</Action>
        </div>
      );
    } else {
      return (
        <div>
          <div>
            <Form
              onSubmit={this.applyCode}
              style={{ flexDirection: 'row', alignItems: 'flex-end' }}
            >
              <Input
                name="coupon-code"
                type="text"
                value={this.state.code}
                onChange={this.updateCode}
                labelStyle={{
                  margin: 0,
                  display: 'inline',
                  maxWidth: '80%',
                  width: '25rem',
                }}
                inputStyle={{
                  margin: 0,
                  fontWeight: 300,
                  fontStyle: 'normal',
                }}
              />
              {this.state.fetching ? (
                <Action>Checking...</Action>
              ) : (
                <Action onClick={this.applyCode}>
                  {this.state.code.length ? 'Apply' : 'Cancel'}
                </Action>
              )}
            </Form>
          </div>
          <P1>{error || <span>&nbsp;</span>}</P1>
        </div>
      );
    }
  }
}

function simpleRound(number, precision = 0) {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/round
  const factor = Math.pow(10, precision);
  const tempNumber = number * factor;
  const roundedTempNumber = Math.round(tempNumber);
  return roundedTempNumber / factor;
}

function Action({ onClick, children }) {
  return (
    <P2
      style={{
        display: 'inline-block',
        fontWeight: 500,
        marginLeft: '2rem',
      }}
    >
      <Link onClick={onClick}>{children}</Link>
    </P2>
  );
}

function SummaryText({ style, children }) {
  return (
    <P1
      style={{
        margin: 0,
        display: 'inline-block',
        ...(style || EMPTY_OBJECT),
      }}
    >
      {children}
    </P1>
  );
}



// WEBPACK FOOTER //
// ./src/components/Payment/Payment.js